import { CheckCircleOutlineRounded, WarningAmberRounded } from "@mui/icons-material";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { useRecognitionRuns, useRerunRecognition } from "@/hooks/useSubscriptionSchedules";
import type { RecognitionRunState } from "@/interfaces/subscriptionSchedules";
import { formatDateTime } from "@/utils/date-format";
import { money, periodLabel, shortDay } from "./shared";

/** The month a run releases: the one before its scheduled date. "2026-10-01" → "2026-09". */
export const releasedPeriod = (scheduledFor: string) => {
  const d = new Date(`${scheduledFor}T12:00:00Z`);
  d.setUTCDate(0);
  return d.toISOString().slice(0, 7);
};

/** "2026-10-01" → "September". */
const releasedMonth = (scheduledFor: string) =>
  new Date(`${releasedPeriod(scheduledFor)}-15T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    timeZone: "UTC",
  });

/** End a message with a full stop, so two can sit side by side. */
const sentence = (text: string) => (/[.!?]$/.test(text.trim()) ? text.trim() : `${text.trim()}.`);

function describe(s: RecognitionRunState): {
  ok: boolean;
  headline: string;
  detail: string | null;
} {
  const month = `The ${shortDay(s.scheduledFor)} run (${releasedMonth(s.scheduledFor)} revenue)`;
  const backlog =
    s.backlogCount > 0
      ? `${s.backlogCount} release${s.backlogCount === 1 ? "" : "s"} (${money(s.backlogAmount)}) for ended months ${s.backlogCount === 1 ? "has" : "have"} not posted.`
      : null;
  switch (s.state) {
    case "succeeded":
      return backlog
        ? {
            ok: false,
            headline: `${month} succeeded, but releases are waiting.`,
            detail: `${backlog} A cycle paid after the run can cause this — rerun to post them.`,
          }
        : {
            ok: true,
            headline: `${month} succeeded${s.run?.finishedAt ? ` on ${formatDateTime(s.run.finishedAt)}` : ""}: ${s.run?.postedCount ?? 0} release${s.run?.postedCount === 1 ? "" : "s"} posted.`,
            detail: null,
          };
    case "failed":
      return {
        ok: false,
        headline: `${month} failed.`,
        detail:
          [s.run?.error ? sentence(s.run.error) : null, backlog].filter(Boolean).join(" ") || null,
      };
    case "missed":
      return {
        ok: false,
        headline: `${month} did not run.`,
        detail: backlog ?? "Rerun it to post the month.",
      };
    case "running":
      return {
        ok: true,
        headline: `${month} is in progress${s.run ? ` (started ${formatDateTime(s.run.startedAt)})` : ""}.`,
        detail: null,
      };
    default:
      return {
        ok: true,
        headline: `${month} is scheduled for today.`,
        detail: backlog,
      };
  }
}

/**
 * Where this month's recognition run stands, between the check and the breakdowns. Green when it
 * ran and nothing is waiting; amber, with Rerun, when it failed, never ran, or releases for ended
 * months are still unposted.
 */
export default function RecognitionRunBar({ onHistory }: { onHistory?: () => void }) {
  const { data } = useRecognitionRuns(1, 1);
  const rerun = useRerunRecognition();
  const { can } = usePermissions();
  const [confirming, setConfirming] = useState(false);
  if (!data) return null;
  const s = data.state;
  const { ok, headline, detail } = describe(s);
  const mayRerun = s.canRerun && can("edit", "FINANCE");

  return (
    <Box
      component="section"
      aria-label="Recognition run"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        mb: 3,
        px: 2,
        py: 1.5,
        borderRadius: "var(--radius-card)",
        border: `1px solid ${ok ? "var(--color-card-border)" : "var(--color-good-ochre)"}`,
        background: ok ? "var(--color-white)" : "var(--color-good-ochre-bg)",
      }}
    >
      {ok ? (
        <CheckCircleOutlineRounded fontSize="small" sx={{ color: "var(--color-good-sage)" }} />
      ) : (
        <WarningAmberRounded fontSize="small" sx={{ color: "var(--color-good-ochre)" }} />
      )}
      <Box sx={{ flex: "1 1 360px", minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{headline}</Typography>
        {detail && (
          <Typography sx={{ fontSize: 12.5, color: "var(--color-rodeo-black)" }}>
            {detail}
          </Typography>
        )}
        <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
          Next run {shortDay(s.nextScheduledFor)} ({periodLabel(s.nextScheduledFor.slice(0, 7))}).{" "}
          {rerun.isError && (
            <Box component="span" sx={{ color: "var(--color-error, #b3261e)" }}>
              {(rerun.error as Error).message}{" "}
            </Box>
          )}
          {rerun.isSuccess && (
            <Box component="span" sx={{ color: "var(--color-good-sage)" }}>
              Rerun {rerun.data.status}: {rerun.data.postedCount} posted.{" "}
            </Box>
          )}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        {mayRerun && !confirming && (
          <Button
            size="small"
            variant="contained"
            onClick={() => setConfirming(true)}
            sx={{
              textTransform: "none",
              background: "var(--color-rodeo-black)",
              "&:hover": { background: "var(--color-rodeo-black)", opacity: 0.9 },
            }}
          >
            Rerun now
          </Button>
        )}
        {mayRerun && confirming && (
          <>
            <Typography sx={{ fontSize: 12.5 }}>
              Posts every release for ended months, dated each month's last day.
            </Typography>
            <Button
              size="small"
              variant="contained"
              disabled={rerun.isPending}
              onClick={() => rerun.mutate(undefined, { onSettled: () => setConfirming(false) })}
              startIcon={
                rerun.isPending ? <CircularProgress size={14} color="inherit" /> : undefined
              }
              sx={{
                textTransform: "none",
                background: "var(--color-rodeo-black)",
                "&:hover": { background: "var(--color-rodeo-black)", opacity: 0.9 },
              }}
            >
              {rerun.isPending ? "Running…" : "Confirm rerun"}
            </Button>
            <Button
              size="small"
              disabled={rerun.isPending}
              onClick={() => setConfirming(false)}
              sx={{ textTransform: "none", color: "var(--color-graphite)" }}
            >
              Cancel
            </Button>
          </>
        )}
        {onHistory && (
          <Button
            size="small"
            onClick={onHistory}
            sx={{ textTransform: "none", color: "var(--color-rodeo-black)", fontWeight: 700 }}
          >
            Run history
          </Button>
        )}
      </Box>
    </Box>
  );
}
